/**
 * Identity pins: the X25519 public key first seen for each collaborator,
 * across every map of this vault (A2 / S-M3, review 2026-09-24).
 *
 * Map authority already pins identities per map. That leaves gaps: an invite
 * to a new map trusts whatever key the server publishes, and an owner's
 * "restore access" re-trusts the server's new key. Pins remember the first
 * key seen for a user, so any map, invitation or restore that disagrees is
 * shown with both fingerprints instead of being accepted silently. A pin
 * changes only when the user confirms they verified the new key out of band.
 *
 * Sources, merged:
 *  - the verified authority state of every map in the vault (encrypted, and
 *    synced to every device through the vault's key journal);
 *  - a per-account record of first-seen keys and confirmed changes, kept on
 *    this device (localStorage). It is not secret: user ids and public keys.
 *    It is what makes "first seen" survive a later change of a map's state.
 *
 * A user seen with different keys in different maps, and not pinned yet, is
 * a conflict: every one of those keys is reported until the user confirms one.
 */

const STORE_PREFIX = 'orbital_identity_pins_v1:';

/** @typedef {{publicKey: string, username?: string, firstSeen: number, verifiedAt?: number, previousKeys?: string[]}} Pin */

function defaultBackend() {
  return {
    read(key) {
      try { return globalThis.localStorage?.getItem(key) ?? null; } catch { return null; }
    },
    write(key, value) {
      try { globalThis.localStorage?.setItem(key, value); } catch { /* storage unavailable: pins stay in memory */ }
    },
  };
}

/**
 * @param {Object} [options]
 * @param {{read(key: string): string|null, write(key: string, value: string): void}} [options.backend]
 * @param {() => number} [options.now]
 */
export function createIdentityPins({ backend = defaultBackend(), now = () => Date.now() } = {}) {
  let account = null;
  /** @type {Map<string, Pin>} */
  let pins = new Map();
  /** Keys seen in verified map states: userId -> Map(publicKey -> Set(docId)). */
  let seen = new Map();

  function load(accountId) {
    if (account === accountId) return;
    account = accountId || null;
    pins = new Map();
    seen = new Map();
    if (!account) return;
    try {
      const stored = JSON.parse(backend.read(STORE_PREFIX + account) || '{}');
      for (const [userId, pin] of Object.entries(stored.pins || {})) {
        if (typeof pin?.publicKey === 'string') pins.set(userId, pin);
      }
    } catch { /* unreadable record: start empty, pins are rebuilt from maps */ }
  }

  function save() {
    if (!account) return;
    try {
      backend.write(STORE_PREFIX + account, JSON.stringify({ v: 1, pins: Object.fromEntries(pins) }));
    } catch { /* storage unavailable: pins stay in memory for this session */ }
  }

  function note(userId, publicKey, docId) {
    if (!seen.has(userId)) seen.set(userId, new Map());
    const keys = seen.get(userId);
    if (!keys.has(publicKey)) keys.set(publicKey, new Set());
    if (docId) keys.get(publicKey).add(docId);
  }

  /** Identities of a verified authority state (witnesses carry every key it pinned). */
  function identitiesOf(state) {
    const names = new Map((state?.members || []).map(m => [m.userId, m.username]));
    return (state?.witnesses || []).map(w => ({ userId: w.userId, publicKey: w.publicKey, username: names.get(w.userId) }));
  }

  /**
   * Status of `publicKey` for `userId`:
   *  - 'match'    pinned (or, unpinned, the only key seen in verified maps);
   *  - 'new'      never seen;
   *  - 'changed'  a different key is pinned;
   *  - 'conflict' unpinned, and verified maps disagree about the key.
   * @returns {{status: string, pinnedKey: string|null, seenKeys: string[], pin: Pin|null}}
   */
  function check(userId, publicKey) {
    const pin = pins.get(userId) || null;
    const seenKeys = [...(seen.get(userId)?.keys() || [])];
    if (pin) return { status: pin.publicKey === publicKey ? 'match' : 'changed', pinnedKey: pin.publicKey, seenKeys, pin };
    if (!seenKeys.length) return { status: 'new', pinnedKey: null, seenKeys, pin: null };
    if (seenKeys.length === 1) {
      return { status: seenKeys[0] === publicKey ? 'match' : 'changed', pinnedKey: seenKeys[0], seenKeys, pin: null };
    }
    return { status: 'conflict', pinnedKey: null, seenKeys, pin: null };
  }

  /**
   * Record identities seen in a committed context (a verified map state, an
   * invitation the user accepted, an invite the user sent). Pins first-seen
   * keys; never replaces a pin.
   * @param {Array<{userId: string, publicKey: string, username?: string}>} identities
   * @param {{docId?: string, self?: string}} [context]
   * @returns {Array<{userId, username, publicKey, pinnedKey, status}>} identities that disagree
   */
  function observe(identities, { docId = null, self = null } = {}) {
    const mismatches = [];
    let changed = false;
    for (const identity of identities || []) {
      if (!identity?.userId || typeof identity.publicKey !== 'string' || identity.userId === self) continue;
      const before = check(identity.userId, identity.publicKey);
      note(identity.userId, identity.publicKey, docId);
      if (before.status === 'new' || (before.status === 'match' && !before.pin)) {
        pins.set(identity.userId, { publicKey: identity.publicKey, username: identity.username, firstSeen: now() });
        changed = true;
      } else if (before.status === 'changed' || before.status === 'conflict') {
        mismatches.push({ userId: identity.userId, username: identity.username || before.pin?.username,
          publicKey: identity.publicKey, pinnedKey: before.pinnedKey, status: before.status });
      }
    }
    if (changed) save();
    return mismatches;
  }

  /**
   * Seed from the verified states of every map of the vault (on sign-in and
   * whenever the vault changes). Pins users seen with a single key;
   * disagreements stay visible as conflicts.
   * @param {Array<{docId: string, keyAuthority?: Object}>} docs
   * @param {string} [self]
   */
  function seed(docs, self = null) {
    for (const doc of docs || []) {
      for (const identity of identitiesOf(doc?.keyAuthority)) {
        if (identity.userId !== self) note(identity.userId, identity.publicKey, doc.docId);
      }
    }
    let changed = false;
    for (const [userId, keys] of seen) {
      if (pins.has(userId) || keys.size !== 1) continue;
      pins.set(userId, { publicKey: [...keys.keys()][0], firstSeen: now() });
      changed = true;
    }
    if (changed) save();
  }

  /**
   * The user confirmed, out of band, that `publicKey` is `userId`'s key.
   * The previous pin is kept in `previousKeys`, never discarded.
   */
  function accept(userId, publicKey, username) {
    const previous = pins.get(userId);
    if (previous?.publicKey === publicKey && previous.verifiedAt) return;
    const previousKeys = [...new Set([...(previous?.previousKeys || []),
      ...(previous && previous.publicKey !== publicKey ? [previous.publicKey] : [])])];
    pins.set(userId, { publicKey, username: username || previous?.username, firstSeen: previous?.firstSeen || now(),
      verifiedAt: now(), ...(previousKeys.length && { previousKeys }) });
    note(userId, publicKey, null);
    save();
  }

  /** Members of a verified map state whose key disagrees with the pins. */
  function mismatchesIn(state, self = null) {
    const mismatches = [];
    for (const identity of identitiesOf(state)) {
      if (identity.userId === self) continue;
      const result = check(identity.userId, identity.publicKey);
      if (result.status === 'changed' || result.status === 'conflict') {
        mismatches.push({ ...identity, pinnedKey: result.pinnedKey, status: result.status, seenKeys: result.seenKeys,
          // The map still uses a key the user replaced after verifying the new one.
          previousKey: !!result.pin?.previousKeys?.includes(identity.publicKey) });
      }
    }
    return mismatches;
  }

  /** `observe` for every identity of a verified authority state. */
  function observeState(state, context) {
    return observe(identitiesOf(state), context);
  }

  return { load, check, observe, observeState, seed, accept, mismatchesIn, get account() { return account; } };
}

export const IdentityPins = createIdentityPins();
